import { useEffect, useState } from "react";

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

/**
 * Lightweight client-side Supabase session hook. The Supabase SDK is imported
 * lazily so public marketing pages keep their small initial bundle.
 */
export function useSupabaseUser() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const toUser = (u: { id: string; email?: string | null; user_metadata?: Record<string, unknown> } | null) =>
        u
          ? {
              id: u.id,
              email: u.email ?? "",
              name:
                (typeof u.user_metadata?.["full_name"] === "string"
                  ? (u.user_metadata["full_name"] as string)
                  : "") || (u.email ?? "").split("@")[0] || "Guest",
            }
          : null;

      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setUser(toUser(data.session?.user ?? null));
      setLoading(false);

      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(toUser(session?.user ?? null));
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    })();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const signOut = async () => {
    const { supabase } = await import("@/integrations/supabase/client");
    await supabase.auth.signOut();
    setUser(null);
  };

  return { user, loading, signOut };
}
